import {
  DropdownMenu,
  DropdownMenuLinkType,
  ChartCard,
} from '@/components/Basic'
import {
  Text,
  SelectBox,
  DoughnutChart,
  HorizontalBar,
  LineChart,
} from '@saasalerts/ui'
import { LabelCount, ProductEventActivity, Event } from '@/components/Graphs'
import { AlertStatus, ALERT_STATUS } from '@saasalerts/common/shared'
import {
  CHART_COLORS,
  DONUT_CHART_COLORS,
  getUniqueProducts,
  productEventActivityToLineChartData,
} from '@/utils/chartMethods'
import {
  getCountryDrilldownOptions,
  getOrganizationDrilldownOptions,
  getFileShareEventDrilldownOptions,
  getProductDrilldownOptions,
  getAccountDrilldownOptions,
} from '@/utils/drilldownReport'
import { formatToUSDate, getLabelsFromLabelCounts } from '@/utils/methods'
import { capitalize, get } from 'lodash'
import { FC, useMemo, useDeferredValue } from 'react'
import { UseFormRegister, UseFormWatch } from 'react-hook-form'
import { AccountMap } from '@/components/Graphs/GoogleMap/Map'
import { Organization } from '@saasalerts/common/manage'
import {
  faCalendar,
  faMap,
  faShapes,
} from '@awesome.me/kit-e270131d0a/icons/classic/regular'

export type RiskDashboardSelectOption = {
  countryAlertStatus: AlertStatus
  accountAlertStatus: AlertStatus
  organizationAlertStatus: AlertStatus
  productAlertStatus: AlertStatus
}
interface RiskDashboardChartsProps {
  criticalAlertsByCountry: {
    data: LabelCount[] | undefined
    error: string | undefined
  }

  potentialRisks: {
    data: Event[] | undefined
    isValidatingPotentialRisks: boolean
    error: string | undefined
  }

  startDate: string
  endDate: string
  organizationAlerts: {
    data: LabelCount[] | undefined
    error: string | undefined
  }
  accountAlerts: { data: LabelCount[] | undefined; error: string | undefined }
  organizationsExternalFileShareEvents: {
    data: LabelCount[] | undefined
    error: string | undefined
  }
  productWiseEventActivities: {
    data: ProductEventActivity[] | undefined
    error: string | undefined
  }
  organizations?: Array<Organization>
}

export const alertType = {
  [ALERT_STATUS.CRITICAL]: {
    id: ALERT_STATUS.CRITICAL,
    name: capitalize(ALERT_STATUS.CRITICAL),
    color: CHART_COLORS.red,
    colors: DONUT_CHART_COLORS.red,
  },
  [ALERT_STATUS.MEDIUM]: {
    id: ALERT_STATUS.MEDIUM,
    name: capitalize(ALERT_STATUS.MEDIUM),
    color: CHART_COLORS.yellow,
    colors: DONUT_CHART_COLORS.yellow,
  },
  [ALERT_STATUS.LOW]: {
    id: ALERT_STATUS.LOW,
    name: capitalize(ALERT_STATUS.LOW),
    color: CHART_COLORS.green,
    colors: DONUT_CHART_COLORS.green,
  },
}

export const RiskDashboardCharts: FC<
  React.PropsWithChildren<{
    data: RiskDashboardChartsProps
    register: UseFormRegister<RiskDashboardSelectOption>
    watch: UseFormWatch<RiskDashboardSelectOption>
  }>
> = ({ data, register, watch }) => {
  const {
    startDate,
    endDate,
    criticalAlertsByCountry,
    potentialRisks,
    organizationAlerts,
    accountAlerts,
    organizationsExternalFileShareEvents,
    productWiseEventActivities,
    organizations: organizations,
  } = data

  const countryAlertStatus = watch('countryAlertStatus')
  const organizationAlertStatus = watch('organizationAlertStatus')
  const accountAlertStatus = watch('accountAlertStatus')
  const productAlertStatus = watch('productAlertStatus')

  // Stable references for frequently passed arrays
  const organizationsSafe = useMemo(() => organizations ?? [], [organizations])
  const potentialRiskEvents = useMemo(
    () => (potentialRisks?.data ?? []).slice(0, 10000),
    [potentialRisks?.data]
  )
  const deferredPotentialRiskEvents = useDeferredValue(potentialRiskEvents)
  const dateRangeLabel = useMemo(
    () => `From ${formatToUSDate(startDate)} to ${formatToUSDate(endDate)}`,
    [startDate, endDate]
  )

  const _countriesByAlerts = useMemo(
    () => criticalAlertsByCountry,
    [criticalAlertsByCountry]
  )
  const _organizationAlerts = useMemo(
    () => organizationAlerts,
    [organizationAlerts]
  )
  const _accountAlerts = useMemo(() => accountAlerts, [accountAlerts])
  const _organizationsExternalFileShareEvents = useMemo(
    () => organizationsExternalFileShareEvents,
    [organizationsExternalFileShareEvents]
  )

  const _productWiseEventActivities = useMemo(
    () => productWiseEventActivities,
    [productWiseEventActivities]
  )

  const alertTypeOptions = useMemo(() => Object.values(alertType), [])

  const countryOptions: DropdownMenuLinkType[] = useMemo(
    () =>
      getCountryDrilldownOptions({
        labels: getLabelsFromLabelCounts(get(_countriesByAlerts, 'data', [])),
        startDate,
        endDate,
        alertStatus: countryAlertStatus,
      }) ?? [],
    [_countriesByAlerts?.data, startDate, endDate, countryAlertStatus]
  )

  const organizationOptions: DropdownMenuLinkType[] = useMemo(
    () =>
      getOrganizationDrilldownOptions({
        labels: getLabelsFromLabelCounts(get(_organizationAlerts, 'data', [])),
        startDate,
        endDate,
        alertStatus: organizationAlertStatus,
        organizations: organizationsSafe,
      }) ?? [],
    [
      _organizationAlerts?.data,
      startDate,
      endDate,
      organizationAlertStatus,
      organizationsSafe,
    ]
  )

  const accountOptions: DropdownMenuLinkType[] = useMemo(
    () =>
      getAccountDrilldownOptions({
        labels: getLabelsFromLabelCounts(get(_accountAlerts, 'data', [])),
        startDate,
        endDate,
        alertStatus: accountAlertStatus,
      }) ?? [],
    [_accountAlerts?.data, startDate, endDate, accountAlertStatus]
  )

  const fileShareEventOptions: DropdownMenuLinkType[] = useMemo(
    () =>
      getFileShareEventDrilldownOptions({
        labels: getLabelsFromLabelCounts(
          get(_organizationsExternalFileShareEvents, 'data', [])
        ),
        startDate,
        endDate,
        alertStatus: accountAlertStatus,
        organizations: organizationsSafe,
      }) ?? [],
    [
      _organizationsExternalFileShareEvents?.data,
      startDate,
      endDate,
      accountAlertStatus,
      organizationsSafe,
    ]
  )

  const productOptions: DropdownMenuLinkType[] = useMemo(
    () =>
      getProductDrilldownOptions({
        labels: getUniqueProducts(get(_productWiseEventActivities, 'data', [])),
        startDate,
        endDate,
        alertStatus: productAlertStatus,
      }) ?? [],
    [_productWiseEventActivities?.data, startDate, endDate, productAlertStatus]
  )

  // Defer heavy datasets to keep UI responsive
  const countriesData = useDeferredValue(_countriesByAlerts?.data ?? [])
  const organizationData = useDeferredValue(_organizationAlerts?.data ?? [])
  const accountData = useDeferredValue(_accountAlerts?.data ?? [])
  const externalShareData = useDeferredValue(
    _organizationsExternalFileShareEvents?.data ?? []
  )

  const countryDoughnutOption = useMemo(
    () => ({
      series: [
        {
          name: `# ${capitalize(countryAlertStatus)} Alerts`,
          color: get(alertType, countryAlertStatus).colors,
        },
      ],
      dataset: {
        dimensions: ['label', 'count'],
        source: countriesData,
      },
    }),
    [countryAlertStatus, countriesData]
  )

  const organizationBarOption = useMemo(
    () => ({
      legend: {
        bottom: 0,
      },
      yAxis: {
        inverse: true,
      },
      series: [
        {
          name: `# ${capitalize(organizationAlertStatus)} Alerts`,
          itemStyle: {
            color: get(alertType, organizationAlertStatus).color,
          },
        },
      ],
      dataset: {
        dimensions: ['label', 'count'],
        source: organizationData,
      },
    }),
    [organizationAlertStatus, organizationData]
  )

  const accountBarOption = useMemo(
    () => ({
      legend: {
        bottom: 0,
      },
      yAxis: {
        inverse: true,
      },
      series: [
        {
          name: `# ${capitalize(accountAlertStatus)} Alerts`,
          itemStyle: {
            color: get(alertType, accountAlertStatus).color,
          },
        },
      ],
      dataset: {
        dimensions: ['label', 'count'],
        source: accountData,
      },
    }),
    [accountAlertStatus, accountData]
  )

  const fileShareBarOption = useMemo(
    () => ({
      legend: {
        bottom: 0,
      },
      yAxis: {
        inverse: true,
      },
      series: [
        {
          name: `# External File Share Events`,
          itemStyle: {
            color: CHART_COLORS.red,
          },
        },
      ],
      dataset: {
        dimensions: ['label', 'count'],
        source: externalShareData,
      },
    }),
    [externalShareData]
  )

  const productLineOption = useMemo(
    () =>
      productEventActivityToLineChartData(_productWiseEventActivities?.data),
    [_productWiseEventActivities?.data]
  )

  return (
    <>
      <ChartCard
        accessory={
          <Text size="sm">*Displaying up to 10,000 latest events</Text>
        }
        accessoryPosition="header"
        header={{
          icon: faMap,
          color: 'brand',
          title: 'Worldwide Risk Events - All Organizations',
        }}
        error={!!potentialRisks?.error}
      >
        <Text color="secondary" className="pb-3 pt-2">
          Updates every 10 seconds and displays worldwide risk events for all
          your organizations in the last 24 hours
        </Text>
        <AccountMap
          events={deferredPotentialRiskEvents}
          loading={
            !!potentialRisks?.isValidatingPotentialRisks ||
            (!potentialRisks?.error && !potentialRisks?.data)
          }
          noData={!!potentialRisks?.data && !potentialRisks.data.length}
          noDataText="There were no events recorded in the last 24 hours."
        />
      </ChartCard>

      <ChartCard
        accessory={
          <div className="flex justify-end gap-4 print:hidden">
            <div className="w-full md:w-44 lg:w-44">
              <SelectBox
                {...register('countryAlertStatus')}
                options={alertTypeOptions}
                label=""
                name="countryAlertStatus"
                displaySelectOption={false}
              />
            </div>
            {/* TODO: show drilldown when alert status is low #63 */}
            {countryAlertStatus !== ALERT_STATUS.LOW && (
              <div className="w-full md:w-36 lg:w-36">
                <DropdownMenu itemList={countryOptions} />
              </div>
            )}
          </div>
        }
        header={{
          title: `Countries with most ${countryAlertStatus.toLowerCase()} alerts`,
          icon: faShapes,
          color: 'brand',
        }}
        error={!!_countriesByAlerts?.error}
      >
        <Text className="pt-2" color="secondary">
          {dateRangeLabel}
        </Text>
        <DoughnutChart
          loading={!_countriesByAlerts?.error && !_countriesByAlerts?.data}
          noData={(_countriesByAlerts?.data?.length ?? 0) === 0}
          noDataText={`There were no ${countryAlertStatus} alerts during the period.`}
          height={600}
          option={countryDoughnutOption}
        />
      </ChartCard>

      <ChartCard
        accessory={
          <div className="flex justify-end gap-4 print:hidden">
            <div className="w-full md:w-44 lg:w-44">
              <SelectBox
                {...register('organizationAlertStatus')}
                options={alertTypeOptions}
                label=""
                name="organizationAlertStatus"
                displaySelectOption={false}
              />
            </div>
            <div className="w-full md:w-36 lg:w-36">
              <DropdownMenu itemList={organizationOptions} />
            </div>
          </div>
        }
        header={{
          title: `Organizations with most ${organizationAlertStatus.toLowerCase()} alerts`,
          icon: faCalendar,
          color: 'brand',
        }}
        error={!!_organizationAlerts?.error}
      >
        <HorizontalBar
          loading={!_organizationAlerts?.error && !_organizationAlerts?.data}
          noData={(_organizationAlerts?.data?.length ?? 0) === 0}
          noDataText={`There were no ${organizationAlertStatus} alerts during the period`}
          height={600}
          option={organizationBarOption}
        />
      </ChartCard>

      <ChartCard
        accessory={
          <div className="flex justify-end gap-4 print:hidden">
            <div className="w-full md:w-44 lg:w-44">
              <SelectBox
                {...register('accountAlertStatus')}
                label=""
                name="accountAlertStatus"
                options={alertTypeOptions}
                displaySelectOption={false}
              />
            </div>
            {/* TODO: show drilldown when alert status is low #63 */}
            {accountAlertStatus !== ALERT_STATUS.LOW && (
              <div className="w-full md:w-36 lg:w-36">
                <DropdownMenu itemList={accountOptions} />
              </div>
            )}
          </div>
        }
        header={{
          title: `Accounts with most ${accountAlertStatus.toLowerCase()} alerts`,
          icon: faCalendar,
          color: 'brand',
        }}
        error={!!_accountAlerts?.error}
      >
        <HorizontalBar
          loading={!_accountAlerts?.error && !_accountAlerts?.data}
          noData={(_accountAlerts?.data?.length ?? 0) === 0}
          noDataText={`There were no ${accountAlertStatus} alerts during the period`}
          height={600}
          option={accountBarOption}
        />
      </ChartCard>

      <ChartCard
        accessory={
          <div className="w-full md:w-36 lg:w-36 print:hidden">
            <DropdownMenu itemList={fileShareEventOptions} />
          </div>
        }
        header={{
          title: 'Organizations with most file share events',
          icon: faCalendar,
          color: 'brand',
        }}
        error={!!_organizationsExternalFileShareEvents?.error}
      >
        <HorizontalBar
          loading={
            !_organizationsExternalFileShareEvents.error &&
            !_organizationsExternalFileShareEvents?.data
          }
          noData={
            (_organizationsExternalFileShareEvents?.data?.length ?? 0) === 0
          }
          noDataText="There were no external file share events by organizations during this period"
          height={600}
          option={fileShareBarOption}
        />
      </ChartCard>

      <ChartCard
        accessory={
          <div className="flex justify-end gap-4 print:hidden">
            <div className="w-full md:w-44 lg:w-44">
              <SelectBox
                {...register('productAlertStatus')}
                label=""
                name="productAlertStatus"
                options={alertTypeOptions}
                displaySelectOption={false}
              />
            </div>
            {/* TODO: show drilldown when alert status is low #63 */}
            {productAlertStatus !== ALERT_STATUS.LOW && (
              <div className="w-full md:w-36 lg:w-36">
                <DropdownMenu itemList={productOptions} />
              </div>
            )}
          </div>
        }
        header={{
          title: 'Product / Events Activity',
          icon: faCalendar,
          color: 'brand',
        }}
        error={!!_productWiseEventActivities.error}
      >
        <LineChart
          loading={
            !_productWiseEventActivities.error &&
            !_productWiseEventActivities?.data
          }
          noData={(_productWiseEventActivities?.data?.length ?? 0) === 0}
          noDataText={`There were no ${productAlertStatus} events recorded during this period`}
          height={600}
          option={productLineOption}
        />
      </ChartCard>
    </>
  )
}
